// import { ResponseGetTutorialsType } from 'api/tutorial';
import appConfig from 'helper/mapBackendConfig';
import { matchPath } from 'react-router';
import { CardTypeTypes } from 'types/Card';
import { TutorialIdsBooleanType, TutorialIdType } from 'types/Tutorial';
import { TUTORIAL_ID, TUTORIAL_ROUTE_MATCH } from 'types/Tutorial';
import { TutorialActionType } from './tutorial.actionCreators';
import {
	LOAD_TUTORIALS_FAILURE,
	LOAD_TUTORIALS_SUCCESS,
	TRIGGER_TUTORIAL_DELAYED,
	UN_TRIGGER_TUTORIAL,
	UPDATE_TUTORIAL_FLAG_FAILURE,
	UPDATE_TUTORIAL_FLAG_INSTANTLY,
} from './tutorial.actionTypes';

export type TriggeredTutorialType = null | TutorialIdType;
export type TutorialStateType = {
	error: null | any;
	triggeredTutorial: TriggeredTutorialType | null;
	areTutorialsLoaded: boolean;
	options: {
		cardType?: CardTypeTypes;
	};
} & {
	[key: string /*TutorialIdType*/]: boolean;
};

export type RootStateType = {
	tutorial: TutorialStateType;
};

export const initialState: TutorialStateType = {
	error: null,
	triggeredTutorial: null,
	areTutorialsLoaded: false,
	options: {} as any,
	[TUTORIAL_ID.FEED_OVERVIEW]: false,
	[TUTORIAL_ID.FEED_CALENDAR]: false,
	[TUTORIAL_ID.CARD_DETAIL]: false,
	[TUTORIAL_ID.MY_SUCCESS]: false,
};

const isDone = (tutorial: TutorialIdType, tutorials: Array<TutorialIdType>) => {
	return tutorials.indexOf(tutorial) >= 0;
};

const getCurrentRoute = (): string => {
	return window.location.pathname.replace(appConfig.env.baseName, '');
};

const isRouteMatching = (triggeredTutorial: TriggeredTutorialType): boolean => {
	if (!triggeredTutorial || triggeredTutorial === null) {
		return false;
	}

	return (
		(TUTORIAL_ROUTE_MATCH[triggeredTutorial] || [])
			.map(
				(path: string) =>
					matchPath(getCurrentRoute(), {
						path,
						exact: true,
						strict: false,
					}) !== null
			)
			.indexOf(true) >= 0
	);
};

const isCardTypeMatching = (triggeredTutorial: TriggeredTutorialType, cardType?: CardTypeTypes): boolean => {
	if (!triggeredTutorial || triggeredTutorial === null) {
		return false;
	}
	if (!cardType) {
		return true;
	}

	return true;
};

const getTriggeredTutorialByPrevStep = (
	tutorials: TutorialIdsBooleanType,
	triggeredTutorial: TriggeredTutorialType,
	stepOrderMap: Array<Array<TutorialIdType>>
) => {
	const mapIndex = stepOrderMap.map((entry: Array<TutorialIdType>) => entry[0] === triggeredTutorial).indexOf(true);
	if (mapIndex < 0) {
		return triggeredTutorial;
	}
	const step1Id = stepOrderMap[mapIndex][0];

	if (triggeredTutorial === step1Id && tutorials[step1Id] === true) {
		return stepOrderMap[mapIndex][1];
	}
	return triggeredTutorial;
};

export const getTutorialFromReducer = (state: TutorialStateType) => ({
	[TUTORIAL_ID.FEED_OVERVIEW]: state[TUTORIAL_ID.FEED_OVERVIEW],
	[TUTORIAL_ID.FEED_CALENDAR]: state[TUTORIAL_ID.FEED_CALENDAR],
	[TUTORIAL_ID.CARD_DETAIL]: state[TUTORIAL_ID.CARD_DETAIL],
	[TUTORIAL_ID.MY_SUCCESS]: state[TUTORIAL_ID.MY_SUCCESS],
});
export const getTutorial = (state: RootStateType): TutorialIdsBooleanType => getTutorialFromReducer(state.tutorial);

export default (state: TutorialStateType = initialState, action: TutorialActionType): Object => {
	switch (action.type) {
		case TRIGGER_TUTORIAL_DELAYED:
			const triggeredTutorial = getTriggeredTutorialByPrevStep(getTutorialFromReducer(state), action.flag, [
				[TUTORIAL_ID.FEED_OVERVIEW, TUTORIAL_ID.FEED_CALENDAR],
				[TUTORIAL_ID.CARD_DETAIL],
			]);
			return {
				...state,
				options: action.options,
				triggeredTutorial:
					isCardTypeMatching(triggeredTutorial, (action.options || state.options || {}).cardType) &&
					isRouteMatching(triggeredTutorial)
						? triggeredTutorial
						: state.triggeredTutorial,
			};
		case UN_TRIGGER_TUTORIAL:
			return {
				...state,
				triggeredTutorial: null,
			};
		case LOAD_TUTORIALS_SUCCESS:
			return {
				...state,
				error: null,
				areTutorialsLoaded: true,
				[TUTORIAL_ID.FEED_OVERVIEW]: isDone(TUTORIAL_ID.FEED_OVERVIEW, action.tutorials.tutorialNames),
				[TUTORIAL_ID.FEED_CALENDAR]: isDone(TUTORIAL_ID.FEED_CALENDAR, action.tutorials.tutorialNames),
				[TUTORIAL_ID.CARD_DETAIL]: isDone(TUTORIAL_ID.CARD_DETAIL, action.tutorials.tutorialNames),
				[TUTORIAL_ID.MY_SUCCESS]: isDone(TUTORIAL_ID.MY_SUCCESS, action.tutorials.tutorialNames),
			};
		case UPDATE_TUTORIAL_FLAG_INSTANTLY:
			return {
				...state,
				[action.flag]: !!action.value,
				triggeredTutorial: action.value === true ? null : state.triggeredTutorial,
			};
		case LOAD_TUTORIALS_FAILURE:
		case UPDATE_TUTORIAL_FLAG_FAILURE:
			return {
				...state,
				error: action.error,
			};
		default:
			return state;
	}
};

export type TriggerTutorialOptionsType = {
	cardType?: CardTypeTypes;
};

export const getTriggeredTutorial = (state: RootStateType): TriggeredTutorialType => state.tutorial.triggeredTutorial;
export const getAreTutorialsLoaded = (state: RootStateType): boolean => state.tutorial.areTutorialsLoaded;
export const getVisibleTutorial = (state: RootStateType): TriggeredTutorialType => {
	const areTutorialsLoaded = getAreTutorialsLoaded(state);
	const tutorial = getTutorial(state);
	const triggeredTutorial = getTriggeredTutorial(state);
	if (!areTutorialsLoaded || triggeredTutorial === null || tutorial[triggeredTutorial] === true) {
		return null;
	}
	return triggeredTutorial;
};
